import React, { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/AuthContext";
import { CheckCircle, Circle, CaretRight, CaretLeft, FloppyDisk, ArrowClockwise, X, Plus, Sparkle } from "@phosphor-icons/react";
import HubSpotContactPicker from "@/components/HubSpotContactPicker";
import VoiceRecorder from "@/components/VoiceRecorder";

const STEPS = [
  { key: "basics", label: "Deal Basics" },
  { key: "understanding", label: "Understanding" },
  { key: "features", label: "Features" },
  { key: "sites", label: "Sites" },
  { key: "dependencies", label: "Dependencies" },
  { key: "outcomes", label: "Outcomes & Success" },
  { key: "commercials", label: "Commercials" },
  { key: "review", label: "Review" },
];

const draftKey = (email) => `sakshi_wizard_draft::${email || "anon"}`;

const isMeaningfulDraft = (d) =>
  !!(d && (
    (d.client_legal_name || "").trim() ||
    (d.recipient_name || "").trim() ||
    (d.executive_summary || "").trim() ||
    (d.understanding || "").trim() ||
    (d.selected_feature_ids || []).length ||
    (d.sites || []).length ||
    (d.pricing_lines_input || []).length
  ));

// Defined at module scope so React doesn't remount them on every keystroke.
const Input = ({ label, value, onChange, type = "text", required, testid, placeholder }) => (
  <label className="block">
    <span className="label-micro">{label}{required && " *"}</span>
    <input
      data-testid={testid}
      type={type}
      value={value ?? ""}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className="mt-1 w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
    />
  </label>
);

const Text = ({ label, value, onChange, rows = 4, testid, placeholder, voice, polish }) => {
  const [polishState, setPolishState] = React.useState({ busy: false, suggestion: null });

  const runPolish = async () => {
    if (!polish || !value || value.trim().length < 8) return;
    if (polishState.suggestion) return; // already have one showing
    setPolishState({ busy: true, suggestion: null });
    try {
      const { data } = await api.post("/ai/polish", {
        text: value, section: polish.section || "generic", client_name: polish.client_name || "",
      });
      const cleaned = (data?.cleaned_output || "").trim();
      if (!cleaned || cleaned === value.trim()) {
        setPolishState({ busy: false, suggestion: null });
        return;
      }
      setPolishState({ busy: false, suggestion: cleaned });
    } catch (e) {
      setPolishState({ busy: false, suggestion: null });
    }
  };

  return (
    <label className="block">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <span className="label-micro">{label}{polish && <span className="ml-2 text-[10px] text-blue-600 font-semibold">AI-polish on blur</span>}</span>
        {voice && (
          <VoiceRecorder value={value} onChange={onChange} prompt={voice.prompt || ""} language={voice.language || "en"} testid={voice.testid || `voice-${testid || "field"}`}/>
        )}
      </div>
      <textarea
        data-testid={testid}
        rows={rows}
        value={value ?? ""}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        onBlur={polish ? runPolish : undefined}
        className="mt-1 w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 leading-relaxed"
      />
      {polishState.busy && (
        <div className="mt-2 text-[11px] text-slate-500 italic" data-testid={`${testid}-polish-busy`}>✨ Polishing with AI…</div>
      )}
      {polishState.suggestion && (
        <div className="mt-2 border border-blue-300 bg-blue-50 rounded-sm p-3 text-xs" data-testid={`${testid}-polish-suggestion`}>
          <div className="label-micro mb-1 text-blue-800">AI polish suggestion</div>
          <div className="text-slate-800 whitespace-pre-wrap leading-relaxed max-h-40 overflow-y-auto">{polishState.suggestion}</div>
          <div className="mt-2 flex gap-2">
            <button type="button" onClick={() => { onChange(polishState.suggestion); setPolishState({ busy: false, suggestion: null }); toast.success("Polished text applied"); }}
                    data-testid={`${testid}-polish-apply`}
                    className="text-xs inline-flex items-center gap-1 bg-slate-900 hover:bg-blue-700 text-white rounded-sm px-2.5 py-1 transition-colors">
              Apply
            </button>
            <button type="button" onClick={() => setPolishState({ busy: false, suggestion: null })} data-testid={`${testid}-polish-keep`}
                    className="text-xs inline-flex items-center gap-1 border border-slate-300 rounded-sm px-2.5 py-1 hover:bg-slate-100">
              Keep original
            </button>
          </div>
        </div>
      )}
    </label>
  );
};

export default function ProposalWizard() {
  const nav = useNavigate();
  const { user } = useAuth();
  const key = draftKey(user?.email);

  const [step, setStep] = useState(0);
  const currentStep = STEPS[step].key;
  const [features, setFeatures] = useState([]);
  const [priceBook, setPriceBook] = useState([]);
  const [suites, setSuites] = useState([]);
  const [approvers, setApprovers] = useState([]);
  const [recovery, setRecovery] = useState(null);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [newFeatureOpen, setNewFeatureOpen] = useState(false);
  const [newFeature, setNewFeature] = useState({ name: "", module: "Vision", client_outcome: "", camera_requirements: "", exclusion_text: "", compute_class: "gpu" });
  const [savingFeature, setSavingFeature] = useState(false);
  const isAdmin = (user?.roles || []).includes("admin");
  const isRestoringRef = useRef(false);
  const [data, setData] = useState({
    proposal_type: "pilot",
    client_legal_name: "",
    recipient_name: "",
    recipient_designation: "",
    vertical_id: "retail",
    deployment_model: "on_prem_edge",
    validity_days: 30,
    hubspot_deal_id: "",
    hubspot_contact_id: "",
    hubspot_company_id: "",
    hubspot_selected: null,
    approver_user_ids: [],
    tax_treatment: "exclusive_gst",
    executive_summary: "",
    understanding: "",
    pilot_outcome: "",
    site_schedule_notes: "",
    post_pilot_pathway: "",
    commercial_terms: "",
    assumptions: [],
    exclusions_ack: [],
    next_steps: [],
    selected_feature_ids: [],
    sites: [],
    sites_mode: "detailed",
    total_site_count: 0,
    dependency_answers: [],
    deselected_dependency_keys: [],
    deployment_details: {},
    success_criteria: [],
    pricing_lines_input: [],
    __exec_auto_drafted: false, // internal: was the exec summary AI-drafted this session?
  });
  const [autoDraftBusy, setAutoDraftBusy] = useState(false);
  const [autoDraftDismissed, setAutoDraftDismissed] = useState(false);
  const [assumptionInput, setAssumptionInput] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/library/features?vertical=retail"),
      api.get("/library/price_book"),
      api.get("/library/suite_templates"),
      api.get("/users/approvers"),
    ]).then(([f, p, s, a]) => { setFeatures(f.data); setPriceBook(p.data); setSuites(s.data); setApprovers(a.data || []); });
  }, []);

  // Draft recovery: check server-side draft first, then localStorage as fallback.
  // Server draft wins when it is newer than the local one (cross-device recovery).
  useEffect(() => {
    if (!user?.email) return;
    (async () => {
      let localDraft = null;
      try {
        const raw = localStorage.getItem(key);
        if (raw) {
          const parsed = JSON.parse(raw);
          if (parsed && isMeaningfulDraft(parsed.data)) localDraft = { ...parsed, source: "local" };
          else localStorage.removeItem(key);
        }
      } catch (err) { console.warn("draft recovery (local) failed", err); }
      let serverDraft = null;
      try {
        const { data: sd } = await api.get("/wizard/draft");
        if (sd && sd.updated_at && isMeaningfulDraft(sd.data || {})) {
          serverDraft = { data: sd.data, step: sd.step || 0, savedAt: sd.updated_at, source: "server" };
        }
      } catch (err) { /* server draft is best-effort */ }
      // Prefer whichever draft is newer
      if (serverDraft && localDraft) {
        setRecovery(new Date(serverDraft.savedAt) >= new Date(localDraft.savedAt) ? serverDraft : localDraft);
      } else if (serverDraft) { setRecovery(serverDraft); }
      else if (localDraft) { setRecovery(localDraft); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.email]);

  // Auto-save to localStorage AND to the server as the user types. localStorage
  // is instant + offline-safe; the server copy survives browser wipes and lets
  // users pick up drafts on a different device.
  const [saveState, setSaveState] = useState("idle"); // idle | saving | saved | error
  useEffect(() => {
    if (!user?.email) return;
    if (isRestoringRef.current) { isRestoringRef.current = false; return; }
    if (!isMeaningfulDraft(data)) return;
    setSaveState("saving");
    const t = setTimeout(async () => {
      const savedAt = new Date().toISOString();
      let localOk = true;
      try { localStorage.setItem(key, JSON.stringify({ data, step, savedAt })); }
      catch (err) { console.warn("local autosave failed", err); localOk = false; }
      let serverOk = true;
      try { await api.put("/wizard/draft", { data, step }); }
      catch (err) { console.warn("server autosave failed", err); serverOk = false; }
      if (localOk || serverOk) {
        setLastSavedAt(savedAt);
        setSaveState("saved");
      } else {
        setSaveState("error");
      }
    }, 700);
    return () => clearTimeout(t);
  }, [data, step, user?.email, key]);

  const resumeDraft = () => {
    if (!recovery) return;
    isRestoringRef.current = true;
    setData(recovery.data);
    setStep(recovery.step || 0);
    setLastSavedAt(recovery.savedAt);
    setRecovery(null);
    toast.success("Draft restored");
  };

  const discardDraft = async () => {
    try { localStorage.removeItem(key); } catch (err) { console.warn("draft clear failed", err); }
    try { await api.delete("/wizard/draft"); } catch (err) { console.warn("server draft clear failed", err); }
    setRecovery(null);
    setLastSavedAt(null);
    toast.info("Saved draft discarded");
  };

  const saveDraftNow = () => {
    try {
      const savedAt = new Date().toISOString();
      localStorage.setItem(key, JSON.stringify({ data, step, savedAt }));
      setLastSavedAt(savedAt);
      setSaveState("saved");
      toast.success("Draft saved locally");
    } catch (err) { console.warn("draft save failed", err); setSaveState("error"); toast.error("Could not save draft (storage full)"); }
  };

  // Auto-draft the Executive Summary once client name + features are set.
  // Fires ONCE per session (guarded by autoDraftDismissed + presence of text).
  const triggerAutoDraft = React.useCallback(async () => {
    if (autoDraftBusy) return;
    const name = (data.client_legal_name || "").trim();
    const fids = data.selected_feature_ids || [];
    if (!name || fids.length === 0) return;
    setAutoDraftBusy(true);
    try {
      const { data: resp } = await api.post("/ai/draft/wizard", {
        section: "exec_summary",
        client_name: name,
        vertical: data.vertical_id || "retail",
        feature_ids: fids,
        site_names: (data.sites || []).map(s => s.site_name).filter(Boolean),
      });
      setData(d => ({ ...d, executive_summary: resp.text || "", __exec_auto_drafted: true }));
      if (resp.warnings?.length) {
        toast.warning(`Draft ready — ${resp.warnings.length} guardrail note(s). Review before sending.`);
      } else {
        toast.success("Executive Summary drafted with Claude. Review before sending.");
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Auto-draft failed. You can still write manually.");
      setAutoDraftDismissed(true);
    } finally { setAutoDraftBusy(false); }
  }, [autoDraftBusy, data.client_legal_name, data.selected_feature_ids, data.vertical_id, data.sites]);

  // Auto-fire once when both prerequisites become true and field is empty
  React.useEffect(() => {
    if (autoDraftBusy || autoDraftDismissed) return;
    if (data.__exec_auto_drafted) return;
    const name = (data.client_legal_name || "").trim();
    const fids = data.selected_feature_ids || [];
    const hasText = (data.executive_summary || "").trim().length > 0;
    if (!name || fids.length === 0 || hasText) return;
    // Only auto-fire when the user actually reaches the understanding step —
    // avoids surprise API calls while they're still in the basics step
    if (currentStep !== "understanding") return;
    const t = setTimeout(() => { triggerAutoDraft(); }, 400);
    return () => clearTimeout(t);
  }, [currentStep, data.client_legal_name, data.selected_feature_ids, data.executive_summary, data.__exec_auto_drafted, autoDraftBusy, autoDraftDismissed, triggerAutoDraft]);

  const createFeatureInline = async () => {
    if (!newFeature.name.trim() || !newFeature.client_outcome.trim()) {
      toast.error("Name and client outcome are required");
      return;
    }
    setSavingFeature(true);
    try {
      const payload = {
        name: newFeature.name.trim(),
        module: newFeature.module || "Vision",
        verticals: ["retail"],
        client_outcome: newFeature.client_outcome.trim(),
        camera_requirements: newFeature.camera_requirements,
        exclusion_text: newFeature.exclusion_text,
        compute_class: newFeature.compute_class,
        dependencies: [],
        status: "live",
        deployment_phase: 1,
        requires_approval: false,
        accuracy_position: "",
        version: 1,
      };
      const { data: created } = await api.post("/library/features", payload);
      setFeatures((prev) => [...prev, created]);
      setData((d) => ({ ...d, selected_feature_ids: [...d.selected_feature_ids, created.id] }));
      toast.success(`"${created.name}" added to library and selected`);
      setNewFeatureOpen(false);
      setNewFeature({ name: "", module: "Vision", client_outcome: "", camera_requirements: "", exclusion_text: "", compute_class: "gpu" });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (detail?.message || "Only admins can create library features."));
    } finally { setSavingFeature(false); }
  };

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const create = async () => {
    setBusy(true);
    try {
      const payload = {
        proposal_type: data.proposal_type, client_legal_name: data.client_legal_name,
        recipient_name: data.recipient_name, recipient_designation: data.recipient_designation,
        vertical_id: "retail", deployment_model: data.deployment_model,
        validity_days: Number(data.validity_days) || 30, hubspot_deal_id: data.hubspot_deal_id || null,
        hubspot_contact_id: data.hubspot_contact_id || null,
        hubspot_company_id: data.hubspot_company_id || null,
        approver_user_ids: data.approver_user_ids || [],
        tax_treatment: data.tax_treatment,
      };
      const { data: created } = await api.post("/proposals", payload);
      const patch = {
        executive_summary: data.executive_summary, understanding: data.understanding,
        pilot_outcome: data.pilot_outcome, site_schedule_notes: data.site_schedule_notes,
        post_pilot_pathway: data.post_pilot_pathway, commercial_terms: data.commercial_terms,
        assumptions: data.assumptions, exclusions_ack: data.exclusions_ack, next_steps: data.next_steps,
        selected_feature_ids: data.selected_feature_ids, sites: data.sites,
        sites_mode: data.sites_mode, total_site_count: Number(data.total_site_count) || 0,
        dependency_answers: data.dependency_answers,
        deselected_dependency_keys: data.deselected_dependency_keys || [],
        deployment_details: data.deployment_details || {},
        success_criteria: data.success_criteria,
      };
      await api.put(`/proposals/${created.id}`, patch);
      if (data.pricing_lines_input.length > 0) {
        const { data: priceResp } = await api.post(`/proposals/${created.id}/pricing/compute`, data.pricing_lines_input);
        if (Array.isArray(priceResp?.errors) && priceResp.errors.length > 0) {
          priceResp.errors.forEach((err) => toast.warning(String(err)));
        }
      }
      try { localStorage.removeItem(key); } catch (err) { console.warn("draft clear failed", err); }
      // Also clear the server-side draft so it doesn't re-surface as recovery
      try { await api.delete("/wizard/draft"); } catch (err) { console.warn("server draft clear failed", err); }
      toast.success("Proposal created");
      nav(`/proposals/${created.id}`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string"
        ? detail
        : (detail?.message || e?.message || "Failed to create proposal");
      toast.error(msg);
    } finally { setBusy(false); }
  };

  const selectedFeatureObjs = useMemo(() => features.filter(f => data.selected_feature_ids.includes(f.id)), [features, data.selected_feature_ids]);
  const requiredDeps = useMemo(() => {
    const items = [];
    for (const f of selectedFeatureObjs) for (const d of (f.dependencies || [])) items.push({ feature: f, dep: d });
    return items;
  }, [selectedFeatureObjs]);

  const setDependencyAnswer = (fid, type, answer, notes = "") => {
    const others = data.dependency_answers.filter(d => !(d.feature_id === fid && d.dependency_type === type));
    set("dependency_answers", [...others, { feature_id: fid, dependency_type: type, answer, notes }]);
  };
  const findDepAnswer = (fid, type) => data.dependency_answers.find(d => d.feature_id === fid && d.dependency_type === type);

  const addSite = () => set("sites", [...data.sites, { id: crypto.randomUUID(), site_name: "", site_code: "", address: "", camera_count: 4, format_or_category: "", deployment_wave: 1, audit_status: "not_started" }]);
  const updateSite = (idx, patch) => set("sites", data.sites.map((s,i) => i===idx ? {...s, ...patch} : s));
  const removeSite = (idx) => set("sites", data.sites.filter((_,i) => i!==idx));

  const addCriterion = () => set("success_criteria", [...data.success_criteria, { id: crypto.randomUUID(), metric: "", target: "", measurement_method: "" }]);
  const updateCriterion = (idx, patch) => set("success_criteria", data.success_criteria.map((s,i) => i===idx ? {...s, ...patch} : s));

  const addPricingLine = (charge_code) => {
    if (data.pricing_lines_input.find(l => l.charge_code === charge_code)) return;
    const pb = priceBook.find(p => p.charge_code === charge_code);
    const qty = smartQty(pb);
    set("pricing_lines_input", [...data.pricing_lines_input, { charge_code, mode: "Standard", quantity: qty, justification: "" }]);
  };
  const updatePricingLine = (idx, patch) => set("pricing_lines_input", data.pricing_lines_input.map((l,i) => i===idx ? {...l, ...patch} : l));
  const removePricingLine = (idx) => set("pricing_lines_input", data.pricing_lines_input.filter((_,i) => i!==idx));

  const totalCameras = data.sites.reduce((a, s) => a + Number(s.camera_count || 0), 0);
  const totalSites = data.sites.length;

  // Smart-quantity: derive qty from basis so per-camera lines use total cameras, per-site lines use total sites.
  const smartQty = (pb) => {
    if (!pb) return 1;
    if (pb.basis === "per_camera") return Math.max(1, totalCameras);
    if (pb.basis === "per_site") return Math.max(1, totalSites);
    return 1;
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-[220px,1fr] min-h-screen" data-testid="wizard-page">
      <aside className="bg-white border-b md:border-b-0 md:border-r border-slate-200 py-4 md:py-6">
        <div className="px-5 label-micro mb-4 hidden md:block">Intake Wizard</div>
        <ol className="flex md:flex-col md:space-y-0 overflow-x-auto md:overflow-visible whitespace-nowrap md:whitespace-normal">
          {STEPS.map((s, i) => (
            <li key={s.key} className="shrink-0 md:shrink">
              <button onClick={() => setStep(i)} data-testid={`step-${s.key}`}
                      className={`w-full flex items-center gap-2 md:gap-3 px-3 md:px-5 py-2.5 text-xs md:text-sm border-l-0 md:border-l-2 border-b-2 md:border-b-0 transition-colors duration-150 ${i===step ? "md:border-blue-600 border-blue-600 bg-blue-50 text-slate-900 font-semibold" : "border-transparent text-slate-600 hover:bg-slate-50"}`}>
                {i < step ? <CheckCircle size={16} weight="fill" className="text-emerald-500" /> : <Circle size={16} weight={i===step?"fill":"regular"} className={i===step?"text-blue-600":""} />}
                <span className="text-xs font-mono text-slate-400 w-4">{String(i+1).padStart(2,'0')}</span>
                <span>{s.label}</span>
              </button>
            </li>
          ))}
        </ol>
      </aside>

      <section className="p-4 sm:p-6 lg:p-8 max-w-4xl w-full min-w-0">
        {recovery && (
          <div className="mb-6 border-l-4 border-blue-500 bg-blue-50 px-4 py-3 flex items-start justify-between gap-4 rounded-sm" data-testid="draft-recovery-banner">
            <div className="flex items-start gap-3">
              <ArrowClockwise size={20} className="text-blue-600 mt-0.5" weight="bold"/>
              <div>
                <div className="font-semibold text-slate-900 text-sm">You have an unfinished draft</div>
                <div className="text-xs text-slate-600 mt-0.5">
                  Saved {recovery.savedAt ? new Date(recovery.savedAt).toLocaleString() : "recently"}
                  {recovery.data?.client_legal_name && <> · <b>{recovery.data.client_legal_name}</b></>}
                  {" · "}Step {(recovery.step || 0) + 1} of {STEPS.length}
                </div>
              </div>
            </div>
            <div className="flex gap-2 shrink-0">
              <button onClick={resumeDraft} data-testid="draft-resume" className="text-xs inline-flex items-center gap-1 bg-slate-900 hover:bg-blue-700 text-white px-3 py-1.5 rounded-sm transition-colors duration-200">
                <ArrowClockwise size={12}/> Resume
              </button>
              <button onClick={discardDraft} data-testid="draft-discard" className="text-xs inline-flex items-center gap-1 border border-slate-300 hover:bg-slate-100 rounded-sm px-3 py-1.5">
                <X size={12}/> Discard
              </button>
            </div>
          </div>
        )}

        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="label-micro">Step {step+1} of {STEPS.length}</div>
            <h2 className="font-display text-2xl sm:text-3xl font-black mt-1">{STEPS[step].label}</h2>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
            <div data-testid="draft-save-status" className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-sm border transition-colors ${
              saveState === "saving" ? "bg-blue-50 border-blue-200 text-blue-700"
              : saveState === "error" ? "bg-red-50 border-red-200 text-red-700"
              : saveState === "saved" && lastSavedAt ? "bg-emerald-50 border-emerald-200 text-emerald-700"
              : "bg-slate-50 border-slate-200 text-slate-500"
            }`}>
              <span className={`w-1.5 h-1.5 rounded-full ${
                saveState === "saving" ? "bg-blue-500 animate-pulse"
                : saveState === "error" ? "bg-red-500"
                : saveState === "saved" ? "bg-emerald-500"
                : "bg-slate-400"
              }`}/>
              {saveState === "saving" ? "Saving draft…"
                : saveState === "error" ? "Save failed — storage full?"
                : lastSavedAt ? `Saved · ${new Date(lastSavedAt).toLocaleTimeString([], {hour: "2-digit", minute: "2-digit", second: "2-digit"})}`
                : "Not saved yet"}
            </div>
            <button onClick={saveDraftNow} data-testid="draft-save-btn" className="text-xs inline-flex items-center gap-1 border border-slate-300 rounded-sm px-3 py-1.5 hover:bg-slate-900 hover:text-white transition-colors duration-200">
              <FloppyDisk size={12}/> Save draft
            </button>
          </div>
        </div>

        <div className="mt-8 space-y-6">
          {currentStep === "basics" && (
            <div className="space-y-4">
              <HubSpotContactPicker
                selected={data.hubspot_selected}
                onSelect={(rec) => {
                  setData((d) => ({
                    ...d,
                    hubspot_selected: rec,
                    hubspot_contact_id: rec.type === "contact" ? rec.id : d.hubspot_contact_id,
                    hubspot_company_id: rec.type === "company" ? rec.id : d.hubspot_company_id,
                    hubspot_deal_id: rec.type === "deal" ? rec.id : d.hubspot_deal_id,
                  }));
                  toast.success(`Linked to HubSpot ${rec.type} · ${rec.label}`);
                }}
                onClear={() => setData((d) => ({ ...d, hubspot_selected: null, hubspot_contact_id: "", hubspot_company_id: "", hubspot_deal_id: "" }))}
                onAutofill={(fields) => setData((d) => ({
                  ...d,
                  client_legal_name: fields.client_legal_name || d.client_legal_name,
                  recipient_name: fields.recipient_name || d.recipient_name,
                  recipient_designation: fields.recipient_designation || d.recipient_designation,
                  hubspot_contact_id: fields.__contact_id || d.hubspot_contact_id,
                  hubspot_company_id: fields.__company_id || d.hubspot_company_id,
                }))}
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input label="Client legal name" required value={data.client_legal_name} onChange={(v)=>set("client_legal_name",v)} testid="w-client-name" />
              <label><span className="label-micro">Proposal type</span>
                <select value={data.proposal_type} onChange={(e)=>set("proposal_type",e.target.value)} className="mt-1 w-full border border-slate-300 rounded-sm px-3 py-2 text-sm" data-testid="w-type">
                  <option value="pilot">Pilot</option><option value="commercial">Commercial</option>
                </select></label>
              <Input label="Recipient name" required value={data.recipient_name} onChange={(v)=>set("recipient_name",v)} testid="w-recipient" />
              <Input label="Recipient designation" value={data.recipient_designation} onChange={(v)=>set("recipient_designation",v)} testid="w-designation" />
              <label className="text-xs text-slate-500 -mt-2 sm:col-span-2 sm:-mt-1">Designation is optional. Add it for more formal proposals or leave blank for quick drafts.</label>
              <label><span className="label-micro">Deployment model</span>
                <select value={data.deployment_model} onChange={(e)=>set("deployment_model",e.target.value)} className="mt-1 w-full border border-slate-300 rounded-sm px-3 py-2 text-sm" data-testid="w-deployment">
                  <option value="on_prem_edge">On-prem / Edge</option><option value="remote_stream">Remote Stream</option>
                </select></label>
              <Input label="Validity (days)" type="number" value={data.validity_days} onChange={(v)=>set("validity_days",v)} testid="w-validity" />
              <Input label="HubSpot deal ID (optional)" value={data.hubspot_deal_id} onChange={(v)=>set("hubspot_deal_id",v)} testid="w-hubspot" />
              <label><span className="label-micro">Tax treatment</span>
                <select value={data.tax_treatment} onChange={(e)=>set("tax_treatment",e.target.value)} className="mt-1 w-full border border-slate-300 rounded-sm px-3 py-2 text-sm" data-testid="w-tax">
                  <option value="exclusive_gst">Exclusive of GST</option><option value="inclusive_gst">Inclusive of GST</option>
                </select></label>
              </div>
            </div>
          )}

          {currentStep === "understanding" && (
            <>
              {autoDraftBusy && (
                <div className="border border-blue-300 bg-blue-50 rounded-sm p-3 text-xs flex items-center gap-2" data-testid="w-exec-autodraft-busy">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse"/>
                  <span className="text-blue-900 font-semibold">Auto-drafting your Executive Summary with Claude…</span>
                  <span className="text-blue-800/70">Client name + selected features detected.</span>
                </div>
              )}
              {data.__exec_auto_drafted && !autoDraftBusy && data.executive_summary && (
                <div className="border border-emerald-300 bg-emerald-50 rounded-sm p-3 text-xs flex items-center justify-between gap-3" data-testid="w-exec-autodraft-banner">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="inline-flex items-center gap-1.5 font-semibold text-emerald-900 shrink-0">
                      <Sparkle size={12} weight="fill"/> Auto-drafted
                    </span>
                    <span className="text-emerald-800/80 truncate">Review, edit or polish before sending. Numbers, prices and dates are never invented.</span>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button type="button" onClick={triggerAutoDraft} data-testid="w-exec-autodraft-redraft" className="text-[11px] font-semibold text-emerald-900 hover:underline">
                      Redraft
                    </button>
                    <button type="button" onClick={() => { set("__exec_auto_drafted", false); }} data-testid="w-exec-autodraft-dismiss" className="text-[11px] text-emerald-900/70 hover:text-emerald-900">
                      Dismiss
                    </button>
                  </div>
                </div>
              )}
              <Text label="Executive summary" rows={5} value={data.executive_summary} onChange={(v)=>{ set("executive_summary",v); if (data.__exec_auto_drafted) set("__exec_auto_drafted", false); }} testid="w-exec-summary" placeholder="Frame the client's problem in their own language…"
                    voice={{ prompt: `Executive summary for a Sakshi.ai retail vision AI proposal for ${data.client_legal_name || "the client"}. Keep the tone formal and business-oriented.`, testid: "voice-exec" }}
                    polish={{ section: "executive_summary", client_name: data.client_legal_name }} />
              {!data.executive_summary && !autoDraftBusy && data.client_legal_name && data.selected_feature_ids.length > 0 && (
                <div className="text-xs text-slate-500 border-l-2 border-blue-500 pl-3 flex items-center justify-between gap-3" data-testid="w-exec-autodraft-hint">
                  <span>💡 Ready to auto-draft the Executive Summary with Claude — client name and features are set.</span>
                  <button type="button" onClick={triggerAutoDraft} data-testid="w-exec-autodraft-trigger" className="text-blue-700 font-semibold hover:underline shrink-0">
                    Draft now
                  </button>
                </div>
              )}
              <Text label="Understanding of requirement" rows={6} value={data.understanding} onChange={(v)=>set("understanding",v)} testid="w-understanding" placeholder="Describe the operational gap and desired outcome…"
                    voice={{ prompt: `Business understanding of a retail vision analytics deployment for ${data.client_legal_name || "the client"}. Reference cameras, sites, POS, shrinkage, dwell, footfall as relevant.`, testid: "voice-understanding" }}
                    polish={{ section: "understanding", client_name: data.client_legal_name }} />
              <div className="text-xs text-slate-500 border-l-2 border-blue-500 pl-3">Tip: Use the <b>Voice</b> button on each field to dictate. Transcription runs on Whisper — after you stop, choose Append or Replace.</div>
            </>
          )}

          {currentStep === "features" && (
            <div className="space-y-2" data-testid="w-features">
              {suites.length > 0 && (
                <div className="bg-slate-50 border border-slate-200 rounded-sm p-4 mb-4" data-testid="w-suites-panel">
                  <div className="label-micro mb-2">Quick-start · Apply a suite template</div>
                  <p className="text-xs text-slate-600 mb-3">Preselects all features in the suite. You can add or remove individual features afterwards.</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {suites.map(s => {
                      const applied = s.feature_ids.length > 0 && s.feature_ids.every(id => data.selected_feature_ids.includes(id));
                      return (
                        <button key={s.id} onClick={() => {
                          const mergedFeatures = Array.from(new Set([...data.selected_feature_ids, ...s.feature_ids]));
                          const defaultCodes = s.default_charge_codes || [];
                          const existingCodes = new Set(data.pricing_lines_input.map(l => l.charge_code));
                          const newLines = defaultCodes
                            .filter(code => !existingCodes.has(code))
                            .map(code => {
                              const pb = priceBook.find(p => p.charge_code === code);
                              return { charge_code: code, mode: "Standard", quantity: smartQty(pb), justification: "" };
                            });
                          const mergedAssumptions = Array.from(new Set([...(data.assumptions || []), ...(s.default_assumptions || [])]));
                          const mergedExclusions = Array.from(new Set([...(data.exclusions_ack || []), ...(s.default_exclusions_ack || [])]));
                          setData((d) => ({
                            ...d,
                            selected_feature_ids: mergedFeatures,
                            pricing_lines_input: [...d.pricing_lines_input, ...newLines],
                            assumptions: mergedAssumptions,
                            exclusions_ack: mergedExclusions,
                          }));
                          const parts = [`${s.feature_ids.length} feature${s.feature_ids.length===1?"":"s"}`];
                          if (newLines.length) parts.push(`${newLines.length} pricing line${newLines.length===1?"":"s"}`);
                          if ((s.default_assumptions||[]).length) parts.push(`${s.default_assumptions.length} assumption${s.default_assumptions.length===1?"":"s"}`);
                          toast.success(`Applied "${s.name}" — ${parts.join(", ")}`);
                        }} data-testid={`w-apply-suite-${s.name.toLowerCase().replace(/\s/g,'-')}`}
                        className={`text-left border rounded-sm px-3 py-2 transition-colors ${applied ? "border-emerald-400 bg-emerald-50" : "border-slate-300 bg-white hover:border-blue-500 hover:bg-blue-50"}`}>
                          <div className="flex items-center justify-between gap-2">
                            <div className="text-sm font-semibold text-slate-900">{s.name}</div>
                            <span className="text-xs text-slate-500 shrink-0">{applied ? "✓ applied" : `${s.feature_ids.length} feats · ${(s.default_charge_codes||[]).length} lines`}</span>
                          </div>
                          <div className="text-xs text-slate-600 mt-1">{s.description || "—"}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <div className="label-micro">Select capabilities for {data.client_legal_name || "the client"} · {data.selected_feature_ids.length} selected</div>
                {isAdmin && (
                  <button onClick={() => setNewFeatureOpen(true)} data-testid="w-new-feature-btn" className="text-xs inline-flex items-center gap-1 border border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white rounded-sm px-3 py-1.5 transition-colors duration-200">
                    <Plus size={12} weight="bold"/> New feature
                  </button>
                )}
              </div>
              {features.map(f => {
                const on = data.selected_feature_ids.includes(f.id);
                return (
                  <div key={f.id} className={`border ${on ? "border-blue-500 bg-blue-50/60" : "border-slate-200 bg-white"} p-4 cursor-pointer transition-colors duration-150 hover:border-blue-400`}
                       onClick={() => set("selected_feature_ids", on ? data.selected_feature_ids.filter(x=>x!==f.id) : [...data.selected_feature_ids, f.id])}
                       data-testid={`feature-${f.name.toLowerCase().replace(/\s/g,'-')}`}>
                    <div className="flex items-start gap-3">
                      <input type="checkbox" checked={on} readOnly className="mt-1"/>
                      <div className="flex-1">
                        <div className="font-semibold text-slate-900">{f.name} <span className="label-micro ml-2">{f.module}</span></div>
                        <p className="text-sm text-slate-600 mt-1">{f.client_outcome}</p>
                        {f.requires_approval && <span className="inline-block mt-2 status-badge status-pending_approval">Requires approval</span>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {currentStep === "sites" && (
            <div className="space-y-4" data-testid="w-sites">
              <div className="bg-white border border-slate-200 p-4">
                <div className="label-micro mb-2">How do you want to describe sites?</div>
                <div className="flex gap-2 flex-wrap">
                  <button type="button" onClick={() => set("sites_mode", "detailed")} data-testid="w-sites-mode-detailed"
                          className={`px-3 py-1.5 text-xs font-semibold rounded-sm border transition-colors ${data.sites_mode === "detailed" ? "bg-slate-900 text-white border-slate-900" : "border-slate-300 hover:border-blue-500"}`}>
                    Per-site details ({data.sites.length})
                  </button>
                  <button type="button" onClick={() => set("sites_mode", "total_only")} data-testid="w-sites-mode-total"
                          className={`px-3 py-1.5 text-xs font-semibold rounded-sm border transition-colors ${data.sites_mode === "total_only" ? "bg-slate-900 text-white border-slate-900" : "border-slate-300 hover:border-blue-500"}`}>
                    Total site count only
                  </button>
                </div>
                <p className="text-xs text-slate-500 mt-2">Use "Total site count only" for early-stage proposals where individual sites aren't finalised yet. You can switch to per-site details later.</p>
              </div>

              {data.sites_mode === "total_only" ? (
                <div className="border border-slate-200 bg-white p-4">
                  <Input label="Total number of sites / locations" required type="number" value={data.total_site_count} onChange={(v)=>set("total_site_count", Number(v) || 0)} testid="w-total-site-count" />
                  <p className="text-xs text-slate-500 mt-2">The proposal will state the total site count. A detailed site schedule can be finalised during onboarding.</p>
                </div>
              ) : (
                <>
                  {data.sites.map((s, i) => (
                    <div key={s.id} className="border border-slate-200 bg-white p-4">
                      <div className="flex items-center justify-between mb-3"><div className="label-micro">Site {i+1}</div>
                        <button onClick={() => removeSite(i)} className="text-xs text-red-600 hover:underline" data-testid={`w-remove-site-${i}`}>Remove</button></div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <Input label="Site name" value={s.site_name} onChange={(v)=>updateSite(i,{site_name:v})} testid={`w-site-name-${i}`} />
                        <Input label="Site code" value={s.site_code} onChange={(v)=>updateSite(i,{site_code:v})} testid={`w-site-code-${i}`} />
                        <Input label="Cameras" type="number" value={s.camera_count} onChange={(v)=>updateSite(i,{camera_count:Number(v)})} testid={`w-site-cams-${i}`} />
                        <Input label="Wave" type="number" value={s.deployment_wave} onChange={(v)=>updateSite(i,{deployment_wave:Number(v)})} testid={`w-site-wave-${i}`} />
                        <div className="sm:col-span-2"><Input label="Address" value={s.address} onChange={(v)=>updateSite(i,{address:v})} testid={`w-site-addr-${i}`} /></div>
                      </div>
                    </div>
                  ))}
                  <button onClick={addSite} className="border border-dashed border-slate-300 w-full py-3 text-sm text-slate-600 hover:border-blue-500 hover:text-blue-600 rounded-sm" data-testid="w-add-site">+ Add site</button>
                </>
              )}
              <Text label="Site schedule notes" value={data.site_schedule_notes} onChange={(v)=>set("site_schedule_notes",v)} testid="w-site-notes"
                    voice={{ prompt: `Site deployment schedule notes for a retail rollout across ${data.sites_mode === "total_only" ? (data.total_site_count || 0) : data.sites.length} site(s). Reference waves, sequencing, and dependencies.`, testid: "voice-site-notes" }}
                    polish={{ section: "site_schedule_notes", client_name: data.client_legal_name }} />
            </div>
          )}

          {currentStep === "dependencies" && (
            <div className="space-y-3" data-testid="w-dependencies">
              {/* Deployment-model-specific mandatory block — always shown at the top */}
              {data.deployment_model === "on_prem_edge" && (
                <div className="border border-blue-300 bg-blue-50/50 p-4" data-testid="w-edge-server-block">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-sm font-semibold text-blue-900">Edge server requirements</div>
                    <span className="text-[10px] font-bold uppercase bg-red-100 text-red-700 px-2 py-0.5 rounded-sm">Required · On-prem</span>
                  </div>
                  <p className="text-xs text-slate-600 mb-3">Selected deployment is <b>On-prem / Edge</b>. Capture the edge hardware the client must provision — these details will land in the Implementation section of the exported proposal.</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Input label="Edge server make" required value={data.deployment_details.edge_server_make || ""} onChange={(v)=>set("deployment_details",{...data.deployment_details,edge_server_make:v})} testid="w-edge-make" placeholder="e.g. Dell, HP, Lenovo" />
                    <Input label="Edge server model" required value={data.deployment_details.edge_server_model || ""} onChange={(v)=>set("deployment_details",{...data.deployment_details,edge_server_model:v})} testid="w-edge-model" placeholder="e.g. PowerEdge R750, ThinkEdge SE450" />
                    <div className="sm:col-span-2">
                      <Input label="Spec (CPU / GPU / RAM / storage)" required value={data.deployment_details.edge_server_spec || ""} onChange={(v)=>set("deployment_details",{...data.deployment_details,edge_server_spec:v})} testid="w-edge-spec" placeholder="e.g. Xeon Gold 6338 · RTX A4000 · 128 GB RAM · 4 TB NVMe" />
                    </div>
                    <Input label="Number of units" required type="number" value={data.deployment_details.edge_server_count || ""} onChange={(v)=>set("deployment_details",{...data.deployment_details,edge_server_count:Number(v)||0})} testid="w-edge-count" placeholder="e.g. 3" />
                    <Input label="Network / connectivity notes" value={data.deployment_details.network_notes || ""} onChange={(v)=>set("deployment_details",{...data.deployment_details,network_notes:v})} testid="w-edge-network" placeholder="e.g. 1 Gbps LAN, static IPs, VLAN details" />
                  </div>
                </div>
              )}
              {data.deployment_model === "remote_stream" && (
                <div className="border border-blue-300 bg-blue-50/50 p-4" data-testid="w-remote-stream-block">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-sm font-semibold text-blue-900">DVR / stream access</div>
                    <span className="text-[10px] font-bold uppercase bg-red-100 text-red-700 px-2 py-0.5 rounded-sm">Required · Remote Stream</span>
                  </div>
                  <p className="text-xs text-slate-600 mb-3">Selected deployment is <b>Remote Stream</b>. Capture DVR make/model and stream credentials — the DVR password is stored securely and <b>never printed</b> in the exported proposal.</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Input label="DVR make" required value={data.deployment_details.dvr_make || ""} onChange={(v)=>set("deployment_details",{...data.deployment_details,dvr_make:v})} testid="w-dvr-make" placeholder="e.g. Hikvision, CP Plus" />
                    <Input label="DVR model" required value={data.deployment_details.dvr_model || ""} onChange={(v)=>set("deployment_details",{...data.deployment_details,dvr_model:v})} testid="w-dvr-model" placeholder="e.g. DS-7B08NI-K2" />
                    <Input label="DVR username" required value={data.deployment_details.dvr_username || ""} onChange={(v)=>set("deployment_details",{...data.deployment_details,dvr_username:v})} testid="w-dvr-username" placeholder="e.g. admin" />
                    <Input label="DVR password" required type="password" value={data.deployment_details.dvr_password || ""} onChange={(v)=>set("deployment_details",{...data.deployment_details,dvr_password:v})} testid="w-dvr-password" placeholder="Stored securely, never printed" />
                    <div className="sm:col-span-2">
                      <Input label="RTSP stream URL" required value={data.deployment_details.rtsp_url || ""} onChange={(v)=>set("deployment_details",{...data.deployment_details,rtsp_url:v})} testid="w-dvr-rtsp" placeholder="rtsp://<host>:554/Streaming/Channels/101" />
                    </div>
                    <div className="sm:col-span-2">
                      <Input label="Stream notes" value={data.deployment_details.stream_notes || ""} onChange={(v)=>set("deployment_details",{...data.deployment_details,stream_notes:v})} testid="w-dvr-notes" placeholder="Public IP / port forwarding / VPN access" />
                    </div>
                  </div>
                </div>
              )}
              {requiredDeps.length === 0 && <div className="text-sm text-slate-500">No feature dependencies to answer for the selected features.</div>}
              {requiredDeps.length > 0 && (
                <p className="text-xs text-slate-500">Uncheck any dependency that doesn't apply to this deal — it will be skipped from validation and the exported proposal.</p>
              )}
              {requiredDeps.map(({feature,dep}) => {
                const ans = findDepAnswer(feature.id, dep.type);
                const depKey = `${feature.id}:${dep.type}`;
                const included = !(data.deselected_dependency_keys || []).includes(depKey);
                const toggleInclude = () => {
                  const cur = new Set(data.deselected_dependency_keys || []);
                  if (cur.has(depKey)) cur.delete(depKey); else cur.add(depKey);
                  set("deselected_dependency_keys", Array.from(cur));
                };
                return (
                  <div key={feature.id+dep.type} className={`border p-4 transition-colors ${included ? "border-slate-200 bg-white" : "border-slate-200 bg-slate-50 opacity-60"}`}>
                    <div className="flex items-start gap-3">
                      <label className="inline-flex items-center gap-2 pt-0.5 cursor-pointer" data-testid={`dep-include-${feature.name.toLowerCase().replace(/\s/g,'-')}-${dep.type}`}>
                        <input type="checkbox" checked={included} onChange={toggleInclude} className="mt-0" />
                        <span className="text-[10px] font-semibold uppercase text-slate-500">Include</span>
                      </label>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm"><span className="font-semibold">{feature.name}</span> · <span className="text-slate-600">{dep.description}</span></div>
                        {included && (
                          <>
                            <div className="mt-3 flex gap-2">
                              {["yes","no","n/a"].map(a => (
                                <button key={a} onClick={() => setDependencyAnswer(feature.id, dep.type, a, ans?.notes || "")}
                                        className={`px-3 py-1.5 text-xs font-semibold rounded-sm border ${ans?.answer===a ? "bg-slate-900 text-white border-slate-900" : "border-slate-300 hover:border-blue-500"}`}
                                        data-testid={`dep-${feature.name.toLowerCase().replace(/\s/g,'-')}-${dep.type}-${a}`}>
                                  {a.toUpperCase()}
                                </button>
                              ))}
                            </div>
                            <Input label="Notes" value={ans?.notes || ""} onChange={(v) => setDependencyAnswer(feature.id, dep.type, ans?.answer || "n/a", v)} />
                          </>
                        )}
                        {!included && <div className="text-xs text-slate-500 mt-1 italic">Excluded from validation and export.</div>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {currentStep === "outcomes" && (
            <>
              {data.proposal_type === "pilot" && (
                <>
                  <Text label="Pilot outcome narrative" value={data.pilot_outcome} onChange={(v)=>set("pilot_outcome",v)} testid="w-pilot-outcome"
                        voice={{ prompt: `Pilot outcome narrative for a retail vision AI pilot with ${data.client_legal_name || "the client"}.`, testid: "voice-pilot" }}
                        polish={{ section: "pilot_outcome", client_name: data.client_legal_name }} />
                  <div>
                    <div className="label-micro mb-2">Success criteria (measurable)</div>
                    {data.success_criteria.map((c, i) => (
                      <div key={c.id} className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-2">
                        <Input label="Metric" value={c.metric} onChange={(v)=>updateCriterion(i,{metric:v})} testid={`w-sc-metric-${i}`} />
                        <Input label="Target" value={c.target} onChange={(v)=>updateCriterion(i,{target:v})} testid={`w-sc-target-${i}`} />
                        <Input label="Method" value={c.measurement_method} onChange={(v)=>updateCriterion(i,{measurement_method:v})} testid={`w-sc-method-${i}`} />
                      </div>
                    ))}
                    <button onClick={addCriterion} className="text-sm text-blue-600 hover:underline" data-testid="w-add-criterion">+ Add criterion</button>
                  </div>
                  <Text label="Post-pilot pathway" rows={3} value={data.post_pilot_pathway} onChange={(v)=>set("post_pilot_pathway",v)} testid="w-post-pilot"
                        voice={{ prompt: "Post-pilot pathway: how the pilot converts into a wider commercial rollout.", testid: "voice-post-pilot" }}
                        polish={{ section: "post_pilot_pathway", client_name: data.client_legal_name }} />
                </>
              )}
              <div>
                <div className="label-micro mb-2">Assumptions</div>
                <ul className="space-y-1">{data.assumptions.map((a,i) => (
                  <li key={i} className="flex items-center gap-2 text-sm"><span className="flex-1">• {a}</span>
                    <button onClick={()=>set("assumptions", data.assumptions.filter((_,j)=>j!==i))} className="text-xs text-red-600">Remove</button></li>
                ))}</ul>
                <div className="flex gap-2 mt-2">
                  <input value={assumptionInput} onChange={(e)=>setAssumptionInput(e.target.value)} placeholder="e.g., Client provides POS access on day 1" className="flex-1 border border-slate-300 rounded-sm px-3 py-2 text-sm" data-testid="w-assumption-input" />
                  <button onClick={()=>{ if(assumptionInput.trim()){ set("assumptions",[...data.assumptions, assumptionInput.trim()]); setAssumptionInput(""); }}} className="px-3 py-2 text-sm border border-slate-300 rounded-sm hover:bg-slate-100" data-testid="w-add-assumption">Add</button>
                </div>
              </div>
            </>
          )}

          {currentStep === "commercials" && (
            <div className="space-y-4 pb-24">
              <div className="text-sm text-slate-600 bg-slate-50 border border-slate-200 p-3 rounded-sm">
                Total sites: <b>{data.sites_mode === "total_only" ? (data.total_site_count || 0) : totalSites}</b> · Total cameras: <b>{totalCameras}</b>
                {data.sites_mode === "total_only" && <span className="ml-2 text-xs text-amber-700">(total-only mode)</span>}
              </div>
              <div>
                <div className="label-micro mb-2">Add lines from price book</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {priceBook.map(p => {
                    const already = data.pricing_lines_input.find(l => l.charge_code === p.charge_code);
                    const rupees = `₹${(p.list_price/100).toLocaleString("en-IN")}`;
                    return (
                      <button key={p.id} onClick={() => addPricingLine(p.charge_code)} disabled={!!already}
                              data-testid={`w-add-pline-${p.charge_code}`}
                              className={`text-left border rounded-sm px-3 py-2 transition-colors ${already ? "border-emerald-300 bg-emerald-50 cursor-default" : "border-slate-300 hover:border-blue-500 hover:bg-blue-50"}`}>
                        <div className="flex items-center justify-between gap-2">
                          <div className="text-sm font-semibold text-slate-900 truncate">{p.label}</div>
                          <div className="text-sm font-bold tabular-nums text-slate-900 shrink-0" data-testid={`w-pb-price-${p.charge_code}`}>{rupees}</div>
                        </div>
                        <div className="flex items-center justify-between mt-1">
                          <span className="label-micro">{p.charge_code} · {p.charge_type} · {p.basis.replace(/_/g,' ')}</span>
                          <span className="text-xs text-slate-500">{already ? "✓ added" : "+ add"}</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="space-y-2">
                {data.pricing_lines_input.map((l, i) => {
                  const pb = priceBook.find(p => p.charge_code === l.charge_code);
                  const unitPaise = l.mode === "Standard" ? (pb?.list_price || 0) : (Number(l.unit_price_override) || (pb?.list_price || 0));
                  const subtotalPaise = unitPaise * (Number(l.quantity) || 1);
                  const belowFloor = pb && unitPaise < pb.floor_price;
                  const fmt = (p) => `₹${(p/100).toLocaleString("en-IN")}`;
                  return (
                    <div key={l.charge_code} className="border border-slate-200 bg-white p-3">
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div className="min-w-0">
                          <div className="text-sm font-semibold">{pb?.label} <span className="label-micro ml-2">{l.charge_code}</span></div>
                          <div className="text-xs text-slate-500 mt-0.5">List: <b className="tabular-nums">{fmt(pb?.list_price || 0)}</b> · Floor: <b className="tabular-nums">{fmt(pb?.floor_price || 0)}</b> · Basis: {pb?.basis?.replace(/_/g,' ')}</div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="label-micro">Subtotal</div>
                          <div className={`font-display text-xl font-bold tabular-nums ${belowFloor ? "text-red-600" : "text-slate-900"}`} data-testid={`w-pline-subtotal-${l.charge_code}`}>{fmt(subtotalPaise)}</div>
                          <div className="text-[10px] text-slate-500">{l.charge_type === "recurring" || pb?.charge_type === "recurring" ? "per month" : "one-time"}</div>
                        </div>
                        <button onClick={() => removePricingLine(i)} className="text-xs text-red-600 hover:underline">Remove</button>
                      </div>
                      {belowFloor && <div className="mt-2 text-xs text-red-600 bg-red-50 border-l-2 border-red-500 px-2 py-1">Unit price is below floor ({fmt(pb.floor_price)}). Use Custom mode with justification.</div>}
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 mt-2">
                        <label><span className="label-micro">Mode</span>
                          <select value={l.mode} onChange={(e) => updatePricingLine(i,{mode:e.target.value})} className="mt-1 w-full border border-slate-300 rounded-sm px-2 py-1.5 text-sm" data-testid={`w-pline-mode-${i}`}>
                            <option>Standard</option><option>Adjusted</option><option>Custom</option>
                          </select></label>
                        <Input label="Qty" type="number" value={l.quantity} onChange={(v) => updatePricingLine(i,{quantity:Number(v)})} testid={`w-pline-qty-${i}`} />
                        {l.mode !== "Standard" && <Input label="Unit price (paise)" type="number" value={l.unit_price_override||""} onChange={(v) => updatePricingLine(i,{unit_price_override:Number(v)})} testid={`w-pline-unit-${i}`} />}
                        {l.mode !== "Standard" && <Input label="Justification" value={l.justification} onChange={(v) => updatePricingLine(i,{justification:v})} testid={`w-pline-just-${i}`} />}
                      </div>
                    </div>
                  );
                })}
              </div>
              <Text label="Commercial terms" value={data.commercial_terms} onChange={(v)=>set("commercial_terms",v)} testid="w-commercial-terms"
                    voice={{ prompt: `Commercial terms for a proposal to ${data.client_legal_name || "the client"} — payment terms, billing cadence, discounts, taxes, etc.`, testid: "voice-commercial-terms" }}
                    polish={{ section: "commercial_terms", client_name: data.client_legal_name }} />

              {/* Sticky running total bar */}
              {(() => {
                const lines = data.pricing_lines_input;
                let oneTime = 0, recurring = 0;
                for (const l of lines) {
                  const pb = priceBook.find(p => p.charge_code === l.charge_code);
                  if (!pb) continue;
                  const unitPaise = l.mode === "Standard" ? pb.list_price : (Number(l.unit_price_override) || pb.list_price);
                  const sub = unitPaise * (Number(l.quantity) || 1);
                  if (pb.charge_type === "one_time") oneTime += sub;
                  else if (pb.charge_type === "recurring") recurring += sub;
                }
                const yearOne = oneTime + recurring * 12;
                const fmt = (p) => `₹${(p/100).toLocaleString("en-IN")}`;
                if (lines.length === 0) return null;
                return (
                  <div className="fixed bottom-0 left-0 right-0 lg:left-64 z-30 bg-white border-t-2 border-slate-900 shadow-lg" data-testid="w-running-total-bar">
                    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-3 grid grid-cols-3 gap-3 items-center">
                      <div>
                        <div className="label-micro">One-time</div>
                        <div className="font-display text-lg sm:text-xl font-bold tabular-nums" data-testid="w-total-onetime">{fmt(oneTime)}</div>
                      </div>
                      <div>
                        <div className="label-micro">Recurring / month</div>
                        <div className="font-display text-lg sm:text-xl font-bold tabular-nums" data-testid="w-total-recurring">{fmt(recurring)}</div>
                      </div>
                      <div className="border-l border-slate-200 pl-3">
                        <div className="label-micro text-blue-700">Year-one estimate</div>
                        <div className="font-display text-lg sm:text-xl font-black tabular-nums text-blue-700" data-testid="w-total-year-one">{fmt(yearOne)}</div>
                      </div>
                    </div>
                    <div className="text-[10px] text-slate-500 text-center pb-1">{lines.length} line{lines.length===1?"":"s"} · one-time + recurring × 12 · client-side estimate</div>
                  </div>
                );
              })()}
            </div>
          )}

          {currentStep === "review" && (
            <div className="space-y-4">
              <div className="border border-slate-200 bg-white p-6">
                <h3 className="font-display text-xl font-bold">Ready to create the proposal?</h3>
                <ul className="text-sm text-slate-700 mt-3 space-y-1">
                  <li>Client: <b>{data.client_legal_name || "—"}</b> · Type: <b>{data.proposal_type}</b></li>
                  <li>Features: <b>{data.selected_feature_ids.length}</b> · Sites: <b>{data.sites.length}</b> · Cameras: <b>{totalCameras}</b></li>
                  <li>Pricing lines: <b>{data.pricing_lines_input.length}</b></li>
                  <li>Dependency answers: <b>{data.dependency_answers.length}</b> · Success criteria: <b>{data.success_criteria.length}</b></li>
                </ul>
                <p className="text-xs text-slate-500 mt-3">The proposal opens in draft mode. Run validation and use AI drafting there before submitting for approval.</p>
                <button disabled={busy} onClick={create} data-testid="w-create-btn" className="mt-5 bg-slate-900 hover:bg-blue-700 text-white text-sm font-semibold px-6 py-2.5 rounded-sm transition-colors duration-200 disabled:opacity-60">
                  {busy ? "Creating…" : "Create proposal"}
                </button>
              </div>

              <div className="border border-slate-200 bg-white p-6" data-testid="w-approvers-panel">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div>
                    <h3 className="font-display font-bold">Assign approvers</h3>
                    <p className="text-xs text-slate-500 mt-0.5">Selected approvers get an in-app notification the moment you submit this proposal for approval. Leave empty to notify <b>all</b> approvers.</p>
                  </div>
                  <span className="text-xs text-slate-500">{data.approver_user_ids.length} selected</span>
                </div>
                {approvers.length === 0 ? (
                  <div className="text-xs text-slate-500 mt-3 border-l-2 border-amber-500 bg-amber-50 pl-3 py-2">No approver-role users yet. Ask an admin to grant the <code>approver</code> role in Users &amp; Roles.</div>
                ) : (
                  <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {approvers.map((a) => {
                      const on = data.approver_user_ids.includes(a.id);
                      return (
                        <button type="button" key={a.id} onClick={() => set("approver_user_ids", on ? data.approver_user_ids.filter(x => x !== a.id) : [...data.approver_user_ids, a.id])}
                                data-testid={`w-approver-${a.id}`}
                                className={`text-left border rounded-sm px-3 py-2 transition-colors ${on ? "border-blue-500 bg-blue-50" : "border-slate-200 hover:border-blue-400"}`}>
                          <div className="flex items-start gap-2">
                            <input type="checkbox" checked={on} readOnly className="mt-1"/>
                            <div className="min-w-0">
                              <div className="text-sm font-semibold text-slate-900 truncate">{a.full_name}</div>
                              <div className="text-xs text-slate-500 truncate">{a.email}</div>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="mt-10 pt-4 border-t border-slate-200 flex items-center justify-between">
          <button disabled={step===0} onClick={() => setStep(step-1)} data-testid="w-back" className="text-sm px-4 py-2 border border-slate-300 rounded-sm disabled:opacity-40 hover:bg-slate-100 inline-flex items-center gap-1"><CaretLeft size={14}/>Back</button>
          {step < STEPS.length-1 && (
            <button onClick={() => setStep(step+1)} data-testid="w-next" className="text-sm px-4 py-2 bg-slate-900 hover:bg-blue-700 text-white rounded-sm inline-flex items-center gap-1 transition-colors duration-200">Next <CaretRight size={14}/></button>
          )}
        </div>

        {newFeatureOpen && (
          <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4" data-testid="w-new-feature-modal">
            <div className="bg-white border border-slate-200 w-full max-w-lg p-6 rounded-sm max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-display text-xl font-bold">New feature</h3>
                <button onClick={() => setNewFeatureOpen(false)} data-testid="w-new-feature-close"><X size={18}/></button>
              </div>
              <p className="text-xs text-slate-500 mb-4">This feature will be added to the Retail library and auto-selected for this proposal.</p>
              <div className="space-y-3">
                <label className="block"><span className="label-micro">Name *</span>
                  <input value={newFeature.name} onChange={(e)=>setNewFeature({...newFeature, name: e.target.value})} data-testid="w-nf-name"
                    className="mt-1 w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"/></label>
                <label className="block"><span className="label-micro">Module</span>
                  <input value={newFeature.module} onChange={(e)=>setNewFeature({...newFeature, module: e.target.value})} data-testid="w-nf-module" placeholder="Vision, Analytics, …"
                    className="mt-1 w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"/></label>
                <label className="block"><span className="label-micro">Client outcome *</span>
                  <textarea rows={3} value={newFeature.client_outcome} onChange={(e)=>setNewFeature({...newFeature, client_outcome: e.target.value})} data-testid="w-nf-outcome" placeholder="What tangible benefit does the client get?"
                    className="mt-1 w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"/></label>
                <label className="block"><span className="label-micro">Camera requirements</span>
                  <textarea rows={2} value={newFeature.camera_requirements} onChange={(e)=>setNewFeature({...newFeature, camera_requirements: e.target.value})} data-testid="w-nf-cam"
                    className="mt-1 w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"/></label>
                <label className="block"><span className="label-micro">Exclusion text (what this feature does NOT cover)</span>
                  <textarea rows={2} value={newFeature.exclusion_text} onChange={(e)=>setNewFeature({...newFeature, exclusion_text: e.target.value})} data-testid="w-nf-excl"
                    className="mt-1 w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"/></label>
                <label className="block"><span className="label-micro">Compute class</span>
                  <select value={newFeature.compute_class} onChange={(e)=>setNewFeature({...newFeature, compute_class: e.target.value})} data-testid="w-nf-compute"
                    className="mt-1 w-full border border-slate-300 rounded-sm px-3 py-2 text-sm">
                    <option value="gpu">GPU</option><option value="cpu">CPU</option>
                  </select></label>
              </div>
              <div className="mt-6 flex justify-end gap-2">
                <button onClick={() => setNewFeatureOpen(false)} className="text-sm px-4 py-2 border border-slate-300 rounded-sm hover:bg-slate-100">Cancel</button>
                <button onClick={createFeatureInline} disabled={savingFeature} data-testid="w-nf-save" className="text-sm px-4 py-2 bg-slate-900 hover:bg-blue-700 text-white rounded-sm transition-colors duration-200 disabled:opacity-60">
                  {savingFeature ? "Saving…" : "Create & select"}
                </button>
              </div>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
